<template>
  <div class="cal-root" ref="root">

    <!-- ══════════════════════════════════════════
         DATE PICKER POPOVER (desktop + mobile)
         Clique sur le label de date dans la top bar
    ══════════════════════════════════════════ -->
    <transition name="dp-pop">
      <div v-if="showDatePicker" class="dp-popover-overlay" @click.self="showDatePicker = false">
        <div class="dp-popover" :style="dpPopoverStyle" ref="dpPopover">
          <!-- Sélecteur rapide d'année -->
          <div class="dpp-year-row">
            <button class="dpp-arrow" @click="dpYear--">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <button class="dpp-year-label" @click="dpPickerMode = dpPickerMode === 'years' ? 'days' : 'years'">
              {{ dpYear }}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                :style="dpPickerMode === 'years' ? 'transform:rotate(180deg)' : ''"
                style="transition:transform 0.15s;opacity:0.45;margin-left:3px"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            <button class="dpp-arrow" @click="dpYear++">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>

          <!-- Grille années -->
          <div v-if="dpPickerMode === 'years'" class="dpp-year-grid">
            <button v-for="y in dpYearRange" :key="y"
              class="dpp-year-cell"
              :class="{ active: y === dpYear }"
              :style="y === dpYear ? { background: accentColor, color: '#fff' } : {}"
              @click="dpYear = y; dpPickerMode = 'months'">{{ y }}</button>
          </div>

          <!-- Grille mois -->
          <div v-else-if="dpPickerMode === 'months'" class="dpp-month-grid">
            <button v-for="(mn, mi) in ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']" :key="mi"
              class="dpp-month-cell"
              :class="{ active: mi === dpMonth }"
              :style="mi === dpMonth ? { background: accentColor, color: '#fff' } : {}"
              @click="dpMonth = mi; dpPickerMode = 'days'">{{ mn }}</button>
          </div>

          <!-- Grille jours -->
          <template v-else>
            <div class="dpp-month-nav">
              <button class="dpp-arrow" @click="dpPrevMonth">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <button class="dpp-month-btn" @click="dpPickerMode = 'months'">
                {{ dpMonthName }}
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="opacity:0.4;margin-left:3px"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <button class="dpp-arrow" @click="dpNextMonth">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
            <div class="dpp-weekdays">
              <span v-for="(d,i) in ['L','M','M','J','V','S','D']" :key="i">{{ d }}</span>
            </div>
            <div class="dpp-days-grid">
              <button v-for="(cell, i) in dpDays" :key="i"
                class="dpp-day-cell"
                :class="{
                  'dpp-empty':    !cell,
                  'dpp-today':    cell && isTodayDate(cell.date),
                  'dpp-selected': cell && cell.date === selectedDate,
                }"
                :style="cell && cell.date === selectedDate ? { background: accentColor, color: '#fff' } : (cell && isTodayDate(cell.date) ? { color: accentColor, fontWeight: 700 } : {})"
                @click="cell && dpSelectDate(cell.date)">
                {{ cell ? cell.day : '' }}
              </button>
            </div>
          </template>

          <button class="dpp-today-btn" @click="dpSelectDate(todayStr)">Aujourd'hui</button>
        </div>
      </div>
    </transition>

    <!-- ══════════════════════════════════════════
         MOBILE SHEET — Date picker
    ══════════════════════════════════════════ -->
    <transition name="sheet">
      <div v-if="showDateSheet" class="sheet-overlay" @click.self="showDateSheet = false">
        <div class="sheet-panel">
          <div class="sheet-handle"></div>
          <div class="sheet-header">
            <span class="sheet-title">Choisir une date</span>
            <button class="sheet-close" @click="showDateSheet = false">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="sheet-dp-body">
            <!-- Réutilise le même picker en mode feuille -->
            <div class="dpp-year-row">
              <button class="dpp-arrow" @click="dpYear--">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <button class="dpp-year-label" @click="dpPickerMode = dpPickerMode === 'years' ? 'days' : 'years'">
                {{ dpYear }}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                  :style="dpPickerMode === 'years' ? 'transform:rotate(180deg)' : ''"
                  style="transition:transform 0.15s;opacity:0.45;margin-left:3px"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <button class="dpp-arrow" @click="dpYear++">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
            <div v-if="dpPickerMode === 'years'" class="dpp-year-grid">
              <button v-for="y in dpYearRange" :key="y" class="dpp-year-cell"
                :class="{ active: y === dpYear }"
                :style="y === dpYear ? { background: accentColor, color: '#fff' } : {}"
                @click="dpYear = y; dpPickerMode = 'months'">{{ y }}</button>
            </div>
            <div v-else-if="dpPickerMode === 'months'" class="dpp-month-grid">
              <button v-for="(mn, mi) in ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']" :key="mi"
                class="dpp-month-cell"
                :class="{ active: mi === dpMonth }"
                :style="mi === dpMonth ? { background: accentColor, color: '#fff' } : {}"
                @click="dpMonth = mi; dpPickerMode = 'days'">{{ mn }}</button>
            </div>
            <template v-else>
              <div class="dpp-month-nav">
                <button class="dpp-arrow" @click="dpPrevMonth">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <button class="dpp-month-btn" @click="dpPickerMode = 'months'">
                  {{ dpMonthName }}
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="opacity:0.4;margin-left:3px"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <button class="dpp-arrow" @click="dpNextMonth">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
              <div class="dpp-weekdays">
                <span v-for="(d,i) in ['L','M','M','J','V','S','D']" :key="i">{{ d }}</span>
              </div>
              <div class="dpp-days-grid">
                <button v-for="(cell, i) in dpDays" :key="i"
                  class="dpp-day-cell"
                  :class="{ 'dpp-empty': !cell, 'dpp-today': cell && isTodayDate(cell.date), 'dpp-selected': cell && cell.date === selectedDate }"
                  :style="cell && cell.date === selectedDate ? { background: accentColor, color: '#fff' } : (cell && isTodayDate(cell.date) ? { color: accentColor, fontWeight: 700 } : {})"
                  @click="cell && dpSelectDate(cell.date); cell && (showDateSheet = false)">{{ cell ? cell.day : '' }}</button>
              </div>
            </template>
            <button class="dpp-today-btn" @click="dpSelectDate(todayStr); showDateSheet = false">Aujourd'hui</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══════════════════════════════════════════
         TOP BAR
    ══════════════════════════════════════════ -->
    <header class="top-bar">
      <!-- Nav arrows + date label cliquable -->
      <div class="tb-nav-group">
        <button class="tb-arrow-btn" @click="offsetPeriod(-1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
        </button>

        <button class="tb-nav-label" ref="navLabelBtn"
          :class="{ 'dp-open': showDatePicker }"
          @click="toggleDatePicker">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="opacity:0.5;flex-shrink:0"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
          <span>{{ navLabel }}</span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
            style="opacity:0.35;flex-shrink:0;transition:transform 0.15s"
            :style="showDatePicker ? 'transform:rotate(180deg)' : ''"><path d="M6 9l6 6 6-6"/></svg>
        </button>

        <button class="tb-arrow-btn" @click="offsetPeriod(1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>

      <div class="tb-right">
        <!-- View toggle : Jour / Semaine / Mois -->
        <div class="tb-view-toggle">
          <button :class="['tb-view-btn', { active: currentType === 'day' }]"   @click="currentType = 'day'">Jour</button>
          <button :class="['tb-view-btn', { active: currentType === 'week' }]"  @click="currentType = 'week'">Semaine</button>
          <button :class="['tb-view-btn', { active: currentType === 'month' }]" @click="currentType = 'month'">Mois</button>
        </div>
        <button class="tb-today-btn" @click="goToday" :class="{ active: isToday }">Aujourd'hui</button>
      </div>
    </header>

    <!-- ══════════════════════════════════════════
         MAIN CONTENT
    ══════════════════════════════════════════ -->
    <div class="cal-body">

      <!-- Loading overlay -->
      <div v-if="loading" class="cal-loader">
        <div class="loader-ring" :style="{ borderTopColor: accentColor }"></div>
        <span class="loader-label">Chargement…</span>
      </div>

      <!-- DESKTOP LAYOUT -->
      <div v-if="!isMobile" class="desktop-layout">

        <!-- ── SIDEBAR : liste des techniciens ── -->
        <aside class="sidebar">
          <div class="sidebar-header">
            <span class="sidebar-title">Techniciens</span>
            <span class="sidebar-count">{{ technicians.length }}</span>
          </div>

          <div class="tech-cards-list">
            <!-- Une carte par technicien -->
            <div v-for="tech in technicians" :key="'tc-'+tech.id"
              class="tech-card"
              :class="{ 'tc-selected': selectedTechId === tech.id }"
              :style="selectedTechId === tech.id
                ? { borderColor: tech.color + '40', background: tech.color + '08' }
                : {}"
              @click="selectTech(tech.id)">
              <div class="tc-avatar" :style="{ background: tech.color }">{{ initials(tech.name) }}</div>
              <div class="tc-meta">
                <div class="tc-name">{{ tech.name }}</div>
                <div class="tc-sub" v-if="tech.role">{{ tech.role }}</div>
              </div>

              <div class="tc-active-bar" v-if="selectedTechId === tech.id" :style="{ background: tech.color }"></div>
            </div>
          </div>
        </aside>

        <!-- ── CALENDAR GRID ── -->
        <div class="cal-grid-wrap">

          <!-- ════ DAY VIEW ════ -->
          <template v-if="currentType === 'day'">
            <div class="day-view">
              <!-- Colonnes : 1 par technicien sélectionné (ou tous si aucun sélectionné) -->
              <div class="day-tech-header">
                <div class="day-time-corner"></div>
                <div v-for="tech in visibleTechs" :key="'dth-'+tech.id"
                  class="day-tech-head-cell"
                  :style="{ borderTopColor: tech.color }">
                  <div class="dthc-avatar" :style="{ background: tech.color }">{{ initials(tech.name) }}</div>
                  <div class="dthc-name">{{ tech.name }}</div>
                  <div class="dthc-count" :style="{ color: tech.color }">{{ dayEvents(tech.id, selectedDate).length }}</div>
                </div>
              </div>
              <div class="day-body" ref="dayBody">
                <div class="day-time-col">
                  <div v-for="h in timeSlots" :key="'dtc-'+h" class="day-time-cell">
                    <span class="time-label">{{ String(h).padStart(2,'0') }}:00</span>
                  </div>
                </div>
                <div v-for="tech in visibleTechs" :key="'dcol-'+tech.id"
                  class="day-tech-col">
                  <div v-for="h in timeSlots" :key="'dh-'+h" class="day-hour-cell"></div>
                  <!-- Now line -->
                  <div v-if="isDateToday(selectedDate) && nowPercent >= 0"
                    class="now-line" :style="{ top: nowPercent + '%', borderColor: tech.color }">
                    <div class="now-dot" :style="{ background: tech.color }"></div>
                  </div>
                  <!-- Events -->
                  <div v-for="ev in dayEventsPositioned(tech.id, selectedDate)" :key="'dev-'+ev.id"
                    class="week-ev"
                    :style="weekEvStyle(ev, tech.color)"
                    @click.stop="$emit('event-click', ev)">
                    <div class="wev-accent" :style="{ background: ev.color || tech.color }"></div>
                    <div class="wev-body">
                      <div class="wev-title">{{ ev.title }}</div>
                      <div class="wev-time" v-if="ev.startTime">{{ ev.startTime.slice(0,5) }}{{ ev.endTime ? '–'+ev.endTime.slice(0,5) : '' }}</div>
                      <div class="wev-client" v-if="ev.client">{{ ev.client }}</div>
                    </div>
                    <div class="wev-status" :class="'s-'+normalizeStatus(ev.status)" :style="normalizeStatus(ev.status)==='planned'?{background:ev.color||tech.color}:{}"></div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- ════ WEEK VIEW ════ -->
          <template v-else-if="currentType === 'week'">
            <div class="week-header">
              <div class="wh-corner"></div>
              <div v-for="day in weekDays" :key="'wh-'+day.date"
                class="wh-cell"
                :class="{ 'wh-today': isDateToday(day.date), 'wh-selected': day.date === selectedDate }"
                @click="selectDate(day.date)">
                <span class="wh-dow">{{ day.dow }}</span>
                <span class="wh-num"
                  :style="isDateToday(day.date) ? { background: accentColor, color: '#fff' }
                          : day.date === selectedDate ? { background: accentColor+'20', color: accentColor } : {}">
                  {{ day.num }}
                </span>
                <span class="wh-count" v-if="allDayEventCount(day.date) > 0" :style="{ color: accentColor }">{{ allDayEventCount(day.date) }}</span>
              </div>
            </div>
            <div class="week-body" ref="weekBody">
              <div class="time-col">
                <div v-for="h in timeSlots" :key="'tc-'+h" class="time-cell">
                  <span class="time-label">{{ String(h).padStart(2,'0') }}:00</span>
                </div>
              </div>
              <div v-for="day in weekDays" :key="'wd-'+day.date"
                class="week-day-col"
                :class="{ 'wdc-today': isDateToday(day.date), 'wdc-selected': day.date === selectedDate, 'wdc-weekend': day.isWeekend }"
                @click="selectDate(day.date)">
                <div v-for="h in timeSlots" :key="'wdc-'+h" class="week-hour-cell"></div>
                <div v-if="isDateToday(day.date) && nowPercent >= 0"
                  class="now-line" :style="{ top: nowPercent + '%', borderColor: accentColor }">
                  <div class="now-dot" :style="{ background: accentColor }"></div>
                </div>
                <!-- Events de tous les techs visibles -->
                <div v-for="ev in allDayEventsPositioned(day.date)" :key="'wev-'+ev.id"
                  class="week-ev"
                  :style="weekEvStyle(ev, getTechColor(ev.technicianId))"
                  @click.stop="$emit('event-click', ev)">
                  <div class="wev-accent" :style="{ background: ev.color || getTechColor(ev.technicianId) }"></div>
                  <div class="wev-body">
                    <div class="wev-title">{{ ev.title }}</div>
                    <div class="wev-time" v-if="ev.startTime">{{ ev.startTime.slice(0,5) }}{{ ev.endTime ? '–'+ev.endTime.slice(0,5) : '' }}</div>
                    <div class="wev-client" v-if="ev.client">{{ ev.client }}</div>
                  </div>
                  <div class="wev-status" :class="'s-'+normalizeStatus(ev.status)"
                    :style="normalizeStatus(ev.status)==='planned' ? { background: ev.color||getTechColor(ev.technicianId) } : {}"></div>
                </div>
              </div>
            </div>
          </template>

          <!-- ════ MONTH VIEW ════ -->
          <template v-else>
            <div class="month-header">
              <div v-for="(d,i) in ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim']" :key="i" class="mh-cell">{{ d }}</div>
            </div>
            <div class="month-grid">
              <div v-for="(cell, i) in monthCells" :key="'mc-'+i"
                class="month-cell"
                :class="{
                  'mc-today':       cell && isDateToday(cell.date),
                  'mc-selected':    cell && cell.date === selectedDate,
                  'mc-other-month': cell && cell.otherMonth,
                  'mc-weekend':     cell && cell.isWeekend,
                }"
                @click="cell && !cell.otherMonth && selectDate(cell.date)">
                <div v-if="cell" class="mc-inner">
                  <div class="mc-num-wrap">
                    <span class="mc-num"
                      :style="isDateToday(cell.date) ? { background: accentColor, color: '#fff' }
                              : cell.date === selectedDate ? { background: accentColor+'20', color: accentColor } : {}">
                      {{ cell.day }}
                    </span>
                    <span v-if="allDayEventCount(cell.date) > 0 && !cell.otherMonth"
                      class="mc-ev-count" :style="{ color: accentColor }">{{ allDayEventCount(cell.date) }}</span>
                  </div>
                  <div class="mc-events" v-if="!cell.otherMonth">
                    <div v-for="ev in monthDayEvents(cell.date)" :key="'mev-'+ev.id"
                      class="mc-ev-pill"
                      :style="{
                        background: (ev.color || getTechColor(ev.technicianId)) + '20',
                        borderColor: (ev.color || getTechColor(ev.technicianId)) + '50',
                        color: ev.color || getTechColor(ev.technicianId)
                      }"
                      @click.stop="$emit('event-click', ev)">
                      <div class="mc-ev-dot" :style="{ background: ev.color || getTechColor(ev.technicianId) }"></div>
                      <span class="mc-ev-label">{{ ev.title }}</span>
                    </div>
                    <div v-if="monthDayOverflow(cell.date) > 0" class="mc-ev-more" :style="{ color: accentColor }">
                      +{{ monthDayOverflow(cell.date) }} de plus
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

        </div>
      </div>

      <!-- MOBILE LAYOUT -->
      <div v-else class="mobile-layout">

        <!-- Tech pills scrollables -->
        <div class="mob-tech-strip">
          <div v-for="tech in technicians" :key="'mts-'+tech.id"
            class="mts-pill"
            :class="{ active: selectedTechId === tech.id }"
            :style="selectedTechId === tech.id ? { borderColor: tech.color, background: tech.color+'14', color: tech.color } : {}"
            @click="selectTech(tech.id)">
            <div class="mts-dot" :style="{ background: tech.color }"></div>
            {{ tech.name.split(' ')[0] }}
          </div>
        </div>

        <!-- Week strip (day + week modes) -->
        <div v-if="currentType !== 'month'" class="mob-week-strip">
          <div v-for="day in weekDays" :key="'mws-'+day.date"
            class="mws-cell"
            :class="{ 'mws-today': isDateToday(day.date), 'mws-selected': day.date === selectedDate }"
            @click="selectDate(day.date)">
            <span class="mws-dow">{{ day.dow.slice(0,1) }}</span>
            <span class="mws-num"
              :style="isDateToday(day.date) ? { background: accentColor, color: '#fff' }
                      : day.date === selectedDate ? { background: accentColor+'20', color: accentColor } : {}">
              {{ day.num }}
            </span>
            <div v-if="allDayEventCount(day.date) > 0" class="mws-dot" :style="{ background: accentColor }"></div>
          </div>
        </div>

        <!-- DAY / WEEK : liste des events du jour sélectionné -->
        <template v-if="currentType !== 'month'">
          <div class="mob-day-list">
            <div class="mdl-header">
              <span class="mdl-label">{{ selectedDayLabel }}</span>
              <span class="mdl-count" :style="{ color: accentColor, background: accentColor + '15' }">{{ allSelectedDayEvents.length }}</span>
            </div>
            <div class="mdl-scroll">
              <div v-if="allSelectedDayEvents.length === 0" class="mdl-empty">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l2 2"/></svg>
                <span>Aucune intervention ce jour</span>
              </div>
              <div v-for="ev in allSelectedDayEvents" :key="'mdl-'+ev.id"
                class="mdl-item" @click="$emit('event-click', ev)">
                <div class="mdl-time-col">
                  <span class="mdl-time-start" v-if="ev.startTime">{{ ev.startTime.slice(0,5) }}</span>
                  <div class="mdl-time-bar" :style="{ background: (ev.color||getTechColor(ev.technicianId))+'30' }"></div>
                  <span class="mdl-time-end" v-if="ev.endTime">{{ ev.endTime.slice(0,5) }}</span>
                </div>
                <div class="mdl-card"
                  :style="{ borderLeftColor: ev.color||getTechColor(ev.technicianId), background: (ev.color||getTechColor(ev.technicianId))+'08' }">
                  <div class="mdl-tech-tag" :style="{ color: getTechColor(ev.technicianId), background: getTechColor(ev.technicianId)+'15' }">
                    {{ getTechName(ev.technicianId) }}
                  </div>
                  <div class="mdl-card-title">{{ ev.title }}</div>
                  <div class="mdl-card-client" v-if="ev.client">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    {{ ev.client }}
                  </div>
                  <div class="mdl-card-tags" v-if="ev.tags && ev.tags.length">
                    <span class="mdl-tag" v-for="t in ev.tags" :key="t"
                      :style="{ color: ev.color||getTechColor(ev.technicianId), background: (ev.color||getTechColor(ev.technicianId))+'15', borderColor: (ev.color||getTechColor(ev.technicianId))+'30' }">{{ t }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- MONTH MOBILE -->
        <template v-else>
          <div class="mob-month-header">
            <div v-for="(d,i) in ['L','M','M','J','V','S','D']" :key="i" class="mmh-cell">{{ d }}</div>
          </div>
          <div class="mob-month-grid">
            <div v-for="(cell, i) in monthCells" :key="'mmc-'+i"
              class="mob-mc"
              :class="{
                'mob-mc-today':    cell && isDateToday(cell.date),
                'mob-mc-selected': cell && cell.date === selectedDate,
                'mob-mc-other':    cell && cell.otherMonth,
                'mob-mc-weekend':  cell && cell.isWeekend,
              }"
              @click="cell && !cell.otherMonth && selectDate(cell.date)">
              <div v-if="cell" class="mob-mc-inner">
                <span class="mob-mc-num"
                  :style="isDateToday(cell.date) ? { background: accentColor, color: '#fff' }
                          : cell.date === selectedDate ? { background: accentColor+'20', color: accentColor } : {}">
                  {{ cell.day }}
                </span>
                <div v-if="allDayEventCount(cell.date) > 0 && !cell.otherMonth" class="mob-mc-dots">
                  <div v-for="n in Math.min(allDayEventCount(cell.date), 3)" :key="n"
                    class="mob-mc-dot" :style="{ background: accentColor }"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="mob-day-mini">
            <div class="mdm-header">
              <span class="mdm-label">{{ selectedDayLabel }}</span>
              <span class="mdm-count" :style="{ color: accentColor }">{{ allSelectedDayEvents.length }}</span>
            </div>
            <div v-if="allSelectedDayEvents.length === 0" class="mdm-empty">Aucune intervention</div>
            <div v-for="ev in allSelectedDayEvents" :key="'mdm-'+ev.id"
              class="mdm-item" @click="$emit('event-click', ev)">
              <div class="mdm-stripe" :style="{ background: ev.color||getTechColor(ev.technicianId) }"></div>
              <div class="mdm-body">
                <div class="mdm-title">{{ ev.title }}</div>
                <div class="mdm-time" v-if="ev.startTime">{{ ev.startTime.slice(0,5) }}{{ ev.endTime ? ' → '+ev.endTime.slice(0,5) : '' }}</div>
              </div>
              <div class="mdm-chevron">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
              </div>
            </div>
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
const MOBILE_BP = 768

export default {
  name: 'TechnicianCalendar',

  props: {
    /**
     * Liste complète des techniciens.
     * Chaque objet : { id, name, role?, color }
     */
    technicians:    { type: Array,   default: () => [] },

    /** Vue initiale : 'day' | 'week' | 'month' */
    type:           { type: String,  default: 'week' },

    /**
     * Events à afficher.
     * Format requis : {
     *   id, date,               // obligatoires — date: 'YYYY-MM-DD'
     *   technicianId,           // obligatoire  — pour associer la couleur
     *   title,                  // obligatoire
     *   startTime?,             // 'HH:MM'
     *   endTime?,               // 'HH:MM'
     *   duration?,              // minutes (fallback si pas d'endTime)
     *   client?, address?,
     *   tags?,                  // string[]
     *   status?,                // 'planned'|'in-progress'|'done'|'cancelled'
     *   color?,                 // surcharge la couleur du tech pour cet event
     *   interventionId?,        // déclenche ouverture modale au clic
     * }
     */
    events:         { type: Array,   default: () => [] },

    loading:        { type: Boolean, default: false },
    startHour:      { type: Number,  default: 7 },
    endHour:        { type: Number,  default: 20 },
    maxMonthEvents: { type: Number,  default: 3 },
    /** Couleur d'accentuation globale (today, selected, etc.) — si non fournie, prend la couleur du 1er tech */
    accentColorProp: { type: String, default: null },
    selectFirstTech:   { type: Boolean, default: false},
  },

  emits: ['event-click', 'date-change', 'period-change', 'update:type', 'tech-select'],

  data() {
    const today = new Date()
    const todayStr = this.toDateStrStatic(today)
    return {
      currentType:      this.type,
      isMobile:         false,
      selectedDate:     todayStr,
      todayStr,

      // Techniciens sélectionnés dans la sidebar (filtre)
      // Par défaut : tous sélectionnés
      selectedTechId: null,

      // Date picker popover
      showDatePicker:   false,
      showDateSheet:    false,
      dpYear:           today.getFullYear(),
      dpMonth:          today.getMonth(),
      dpPickerMode:     'days',  // 'days' | 'months' | 'years'
      dpPopoverStyle:   {},

      nowPercent:       -1,
      nowInterval:      null,
    }
  },

  computed: {
    accentColor() {
      if (this.accentColorProp) return this.accentColorProp
      return (this.technicians[0] && this.technicians[0].color) || '#3b82f6'
    },

    timeSlots() {
      const h = []
      for (let i = this.startHour; i <= this.endHour; i++) h.push(i)
      return h
    },
    totalHours() { return this.endHour - this.startHour },

    // Techs effectivement visibles (si aucun filtre = tous)
    visibleTechs() {
      if (this.selectedTechId === null) return this.technicians
      return this.technicians.filter(t => t.id === this.selectedTechId)
    },

    // Events filtrés sur les techs visibles
    // technicianId peut être absent → on affiche quand même si selectedTechId === null
    filteredEvents() {
      if (this.selectedTechId === null) return this.events
      return this.events.filter(e =>
        e.technicianId != null
          ? String(e.technicianId) === String(this.selectedTechId)
          : true  // pas de technicianId → toujours visible
      )
    },

    // ── Date picker computed ──
    dpMonthName() {
      return new Date(this.dpYear, this.dpMonth, 1)
        .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    },
    dpDays() {
      const days = []
      const firstDay = new Date(this.dpYear, this.dpMonth, 1)
      const startDow = (firstDay.getDay() + 6) % 7
      for (let i = 0; i < startDow; i++) days.push(null)
      const dim = new Date(this.dpYear, this.dpMonth + 1, 0).getDate()
      for (let d = 1; d <= dim; d++) {
        days.push({ day: d, date: this.toDateStrStatic(new Date(this.dpYear, this.dpMonth, d)) })
      }
      return days
    },
    dpYearRange() {
      const base = this.dpYear - (this.dpYear % 12)
      const years = []
      for (let i = 0; i < 12; i++) years.push(base + i)
      return years
    },

    // ── Week ──
    weekStart() {
      const d = new Date(this.selectedDate + 'T00:00:00')
      const dow = (d.getDay() + 6) % 7
      d.setDate(d.getDate() - dow)
      return d
    },
    weekDays() {
      const days = []
      const start = new Date(this.weekStart)
      const dows = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim']
      for (let i = 0; i < 7; i++) {
        const d = new Date(start); d.setDate(d.getDate() + i)
        days.push({
          date: this.toDateStrStatic(d),
          dow: dows[i], num: d.getDate(), isWeekend: i >= 5,
        })
      }
      return days
    },

    // ── Month ──
    monthCells() {
      const cells = []
      const firstDay = new Date(this.dpYear, this.dpMonth, 1)
      const startDow = (firstDay.getDay() + 6) % 7
      const dim = new Date(this.dpYear, this.dpMonth + 1, 0).getDate()

      const prevDim = new Date(this.dpYear, this.dpMonth, 0).getDate()
      for (let i = 0; i < startDow; i++) {
        const pm = this.dpMonth === 0 ? 12 : this.dpMonth
        const py = this.dpMonth === 0 ? this.dpYear - 1 : this.dpYear
        const d = prevDim - startDow + 1 + i
        cells.push({ day: d, date: `${py}-${this.pad(pm)}-${this.pad(d)}`, otherMonth: true, isWeekend: i >= 5 })
      }
      for (let d = 1; d <= dim; d++) {
        const colIdx = (startDow + d - 1) % 7
        cells.push({ day: d, date: `${this.dpYear}-${this.pad(this.dpMonth+1)}-${this.pad(d)}`, otherMonth: false, isWeekend: colIdx >= 5 })
      }
      const remainder = (7 - (cells.length % 7)) % 7
      for (let i = 1; i <= remainder; i++) {
        const nm = this.dpMonth === 11 ? 1 : this.dpMonth + 2
        const ny = this.dpMonth === 11 ? this.dpYear + 1 : this.dpYear
        const colIdx = (cells.length + i - 1) % 7
        cells.push({ day: i, date: `${ny}-${this.pad(nm)}-${this.pad(i)}`, otherMonth: true, isWeekend: colIdx >= 5 })
      }
      return cells
    },

    isToday() { return this.selectedDate === this.todayStr },

    navLabel() {
      if (this.currentType === 'day') {
        const d = new Date(this.selectedDate + 'T00:00:00')
        return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      }
      if (this.currentType === 'week') {
        const s = this.weekDays[0], e = this.weekDays[6]
        const ds = new Date(s.date + 'T00:00:00'), de = new Date(e.date + 'T00:00:00')
        if (ds.getMonth() === de.getMonth())
          return `${ds.getDate()} – ${de.getDate()} ${de.toLocaleDateString('fr-FR',{month:'long',year:'numeric'})}`
        return `${ds.toLocaleDateString('fr-FR',{day:'numeric',month:'short'})} – ${de.toLocaleDateString('fr-FR',{day:'numeric',month:'short',year:'numeric'})}`
      }
      return new Date(this.dpYear, this.dpMonth, 1)
        .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    },

    selectedDayLabel() {
      return new Date(this.selectedDate + 'T00:00:00')
        .toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
    },

    allSelectedDayEvents() {
      return this.filteredEvents
        .filter(e => e.date === this.selectedDate)
        .sort((a,b) => (a.startTime||'').localeCompare(b.startTime||''))
    },

    periodEventCount() {
      if (this.currentType === 'day') return this.allSelectedDayEvents.length
      if (this.currentType === 'week') {
        const dates = new Set(this.weekDays.map(d => d.date))
        return this.filteredEvents.filter(e => dates.has(e.date)).length
      }
      const prefix = `${this.dpYear}-${this.pad(this.dpMonth+1)}-`
      return this.filteredEvents.filter(e => e.date && e.date.startsWith(prefix)).length
    },
  },

  watch: {
    type(v) { this.currentType = v },
    currentType(v) {
      this.$emit('update:type', v)
      this.emitPeriodChange()
    },
    selectedDate(v) { this.$emit('date-change', v) },
    technicians: {
      immediate: true,
      handler() {
        // Pas de sélection par défaut — null = tous visibles
      }
    },
  },

  methods: {
    pad(n) { return String(n).padStart(2,'0') },
    toDateStrStatic(date) {
      return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`
    },
    initials(name) { return name.split(' ').map(p=>p[0]).join('').toUpperCase().slice(0,2) },
    isTodayDate(dateStr) { return dateStr === this.todayStr },
    isDateToday(dateStr) { return dateStr === this.todayStr },
    getTechColor(techId) {
      if (!techId) return this.accentColor
      const t = this.technicians.find(t => String(t.id) === String(techId))
      return (t && t.color) || this.accentColor
    },
    getTechName(techId) {
      const t = this.technicians.find(t => t.id === techId)
      return t ? t.name : ''
    },

    // ── Sélection technicien (un seul) ──
    selectTech(id) {
      this.selectedTechId = id
      const tech = id ? (this.technicians.find(t => t.id === id) || null) : null
      this.$emit('tech-select', tech)
    },

    // ── Date picker popover ──
    toggleDatePicker() {
      if (this.isMobile) { this.showDateSheet = true; return }
      if (!this.showDatePicker) {
        // Positionner sous le bouton
        const btn = this.$refs.navLabelBtn
        if (btn) {
          const rect = btn.getBoundingClientRect()
          this.dpPopoverStyle = {
            position: 'fixed',
            top: (rect.bottom + 6) + 'px',
            left: Math.max(8, rect.left + rect.width/2 - 150) + 'px',
          }
        }
        this.dpPickerMode = 'days'
      }
      this.showDatePicker = !this.showDatePicker
    },
    dpPrevMonth() {
      if (this.dpMonth === 0) { this.dpMonth = 11; this.dpYear-- }
      else this.dpMonth--
    },
    dpNextMonth() {
      if (this.dpMonth === 11) { this.dpMonth = 0; this.dpYear++ }
      else this.dpMonth++
    },
    dpSelectDate(dateStr) {
      this.showDatePicker = false
      this.showDateSheet = false
      this.selectDate(dateStr)
    },

    // ── Navigation ──
    selectDate(dateStr) {
      if (!dateStr) return
      this.selectedDate = dateStr
      const d = new Date(dateStr + 'T00:00:00')
      this.dpYear  = d.getFullYear()
      this.dpMonth = d.getMonth()
      this.emitPeriodChange()
    },

    goToday() {
      const t = new Date()
      this.dpYear  = t.getFullYear()
      this.dpMonth = t.getMonth()
      this.selectedDate = this.toDateStrStatic(t)
      this.$emit('date-change', this.selectedDate)
      this.emitPeriodChange()
    },

    offsetPeriod(delta) {
      if (this.currentType === 'day') {
        const d = new Date(this.selectedDate + 'T00:00:00')
        d.setDate(d.getDate() + delta)
        this.selectDate(this.toDateStrStatic(d))
      } else if (this.currentType === 'week') {
        const d = new Date(this.selectedDate + 'T00:00:00')
        d.setDate(d.getDate() + delta * 7)
        this.selectDate(this.toDateStrStatic(d))
      } else {
        let m = this.dpMonth + delta, y = this.dpYear
        if (m < 0)  { m = 11; y-- }
        if (m > 11) { m = 0;  y++ }
        this.dpMonth = m; this.dpYear = y
        this.selectedDate = `${y}-${this.pad(m+1)}-01`
        this.emitPeriodChange()
      }
    },

    emitPeriodChange() {
      if (this.currentType === 'day') {
        this.$emit('period-change', { type: 'day', start: this.selectedDate, end: this.selectedDate })
      } else if (this.currentType === 'week') {
        this.$emit('period-change', { type: 'week', start: this.weekDays[0].date, end: this.weekDays[6].date })
      } else {
        const lastDay = new Date(this.dpYear, this.dpMonth + 1, 0).getDate()
        this.$emit('period-change', {
          type: 'month',
          start: `${this.dpYear}-${this.pad(this.dpMonth+1)}-01`,
          end:   `${this.dpYear}-${this.pad(this.dpMonth+1)}-${this.pad(lastDay)}`,
        })
      }
    },

    // ── Event helpers ──
    dayEvents(techId, date) {
      return this.events.filter(e => String(e.technicianId) === String(techId) && e.date === date)
    },
    dayEventsPositioned(techId, date) {
      return this.dayEvents(techId, date).sort((a,b) => (a.startTime||'').localeCompare(b.startTime||''))
    },
    allDayEventsPositioned(date) {
      return this.filteredEvents
        .filter(e => e.date === date)
        .sort((a,b) => (a.startTime||'').localeCompare(b.startTime||''))
    },
    allDayEventCount(date) {
      return this.filteredEvents.filter(e => e.date === date).length
    },
    techEventCount(techId) {
      return this.events.filter(e => String(e.technicianId) === String(techId)).length
    },
    monthDayEvents(date) {
      return this.filteredEvents
        .filter(e => e.date === date)
        .sort((a,b) => (a.startTime||'').localeCompare(b.startTime||''))
        .slice(0, this.maxMonthEvents)
    },
    monthDayOverflow(date) {
      return Math.max(0, this.filteredEvents.filter(e => e.date === date).length - this.maxMonthEvents)
    },

    // Normalise les statuts FR/EN vers les classes CSS
    normalizeStatus(status) {
      if (!status) return 'planned'
      const s = String(status).toLowerCase()
      if (s.includes('progress') || s.includes('cours')) return 'in-progress'
      if (s.includes('done') || s.includes('terminé') || s.includes('termine')) return 'done'
      if (s.includes('cancel') || s.includes('annul')) return 'cancelled'
      if (s.includes('facture') || s.includes('paiement') || s.includes('envoy')) return 'done'
      return 'planned'
    },

        weekEvStyle(ev, techColor) {
      if (!ev.startTime) return { top: '2px', height: '48px' }
      const [sh, sm] = ev.startTime.split(':').map(Number)
      const startMins = (sh - this.startHour) * 60 + sm
      const totalMins = this.totalHours * 60
      const top = (startMins / totalMins) * 100
      let dur = 60
      if (ev.endTime) {
        const [eh, em] = ev.endTime.split(':').map(Number)
        dur = (eh * 60 + em) - (sh * 60 + sm)
      } else if (ev.duration) { dur = ev.duration }
      const height = Math.max(1.5, (dur / totalMins) * 100)
      const c = ev.color || techColor
      return {
        top: `calc(${top}% + 1px)`,
        height: `calc(${height}% - 2px)`,
        '--ec': c, background: c + '18', borderColor: c + '50',
      }
    },

    updateNow() {
      const now = new Date()
      const mins = (now.getHours() - this.startHour) * 60 + now.getMinutes()
      const total = this.totalHours * 60
      this.nowPercent = (mins >= 0 && mins <= total) ? (mins / total) * 100 : -1
    },

    checkMobile() { this.isMobile = window.innerWidth < MOBILE_BP },

    _onDocClick(e) {
      if (!this.showDatePicker) return
      const pop = this.$refs.dpPopover
      const btn = this.$refs.navLabelBtn
      if (pop && !pop.contains(e.target) && btn && !btn.contains(e.target)) {
        this.showDatePicker = false
      }
    },
  },

  mounted() {
    this.checkMobile()
    this._resizeListener = () => this.checkMobile()
    window.addEventListener('resize', this._resizeListener)
    document.addEventListener('click', this._onDocClick, true)
    this.updateNow()
    this.nowInterval = setInterval(this.updateNow, 60000)
    this.emitPeriodChange()
    this.$nextTick(() => {
      const scroll = this.$refs.weekBody || this.$refs.dayBody
      if (scroll) {
        const cellH = scroll.querySelector('.week-hour-cell, .day-hour-cell')
        if (cellH) scroll.scrollTop = Math.max(0, (this.startHour) * cellH.getBoundingClientRect().height - 40)
      }
    })
    if (this.selectFirstTech && this.technicians.length > 0) {
      this.selectTech(this.technicians[0].id)
    }
  },

  beforeUnmount() {
    clearInterval(this.nowInterval)
    window.removeEventListener('resize', this._resizeListener)
    document.removeEventListener('click', this._onDocClick, true)
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.cal-root {
  font-family: 'Sora', sans-serif;
  display: flex; flex-direction: column;
  height: 100dvh; min-height: 400px;
  background: #f4f5f8; color: #1a1d27;
  overflow: hidden; user-select: none;
}

/* ══ TOP BAR ══ */
.top-bar {
  display: flex; align-items: center; gap: 8px; justify-content: space-between;
  padding: 0 14px; height: 54px;
  background: #fff; border-bottom: 1px solid #e4e7ef;
  flex-shrink: 0; z-index: 20; position: relative;
  box-shadow: 0 1px 8px rgba(0,0,0,0.05);
}
.tb-nav-group { display: flex; align-items: center; gap: 4px; }
.tb-arrow-btn {
  width: 32px; height: 32px; border-radius: 8px;
  border: 1px solid #e4e7ef; background: #f7f8fb;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #64748b; transition: all 0.12s;
}
.tb-arrow-btn:hover { background: #eff4ff; border-color: #3b82f6; color: #3b82f6; }
.tb-arrow-btn:active { transform: scale(0.92); }

/* Label de date cliquable */
.tb-nav-label {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 9px;
  border: 1px solid #e4e7ef; background: #f7f8fb;
  font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 600; color: #1a1d27;
  cursor: pointer; transition: all 0.15s; white-space: nowrap; text-transform: capitalize;
}
.tb-nav-label:hover, .tb-nav-label.dp-open { background: #eff4ff; border-color: #3b82f6; color: #3b82f6; }

.tb-right { display: flex; align-items: center; gap: 8px; }
.tb-view-toggle {
  display: flex; align-items: center;
  background: #f4f5f8; border: 1px solid #e4e7ef; border-radius: 9px; padding: 3px; gap: 2px;
}
.tb-view-btn {
  padding: 5px 12px; border-radius: 6px; border: none; background: transparent;
  font-family: 'Sora', sans-serif; font-size: 12px; font-weight: 500; color: #94a3b8;
  cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.tb-view-btn.active { background: #fff; color: #1a1d27; font-weight: 700; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
.tb-today-btn {
  padding: 7px 13px; border-radius: 8px; border: 1px solid #e4e7ef;
  background: #fff; color: #64748b; cursor: pointer;
  font-family: 'Sora', sans-serif; font-size: 12px; font-weight: 500; transition: all 0.12s;
}
.tb-today-btn:hover { background: #eff4ff; border-color: #3b82f6; color: #3b82f6; }
.tb-today-btn.active { border-color: #3b82f6; color: #3b82f6; background: #eff6ff; font-weight: 600; }

/* ══ DATE PICKER POPOVER ══ */
.dp-popover-overlay {
  position: fixed; inset: 0; z-index: 200;
  /* overlay transparent pour fermer au clic extérieur */
}
.dp-popover {
  position: fixed; z-index: 201;
  width: 300px;
  background: #fff; border: 1px solid #e4e7ef; border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06);
  padding: 14px 16px 16px;
  font-family: 'Sora', sans-serif;
}

/* Ligne année avec flèches */
.dpp-year-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.dpp-arrow {
  width: 28px; height: 28px; border-radius: 7px; border: 1px solid #e4e7ef; background: #f7f8fb;
  display: flex; align-items: center; justify-content: center; cursor: pointer; color: #64748b;
  transition: all 0.12s;
}
.dpp-arrow:hover { background: #eff4ff; border-color: #3b82f6; color: #3b82f6; }
.dpp-year-label {
  flex: 1; text-align: center; font-size: 15px; font-weight: 700; color: #1a1d27;
  background: none; border: none; cursor: pointer; display: flex; align-items: center;
  justify-content: center; font-family: 'Sora', sans-serif; border-radius: 7px;
  padding: 4px 8px; transition: background 0.1s;
}
.dpp-year-label:hover { background: #f1f5f9; }

/* Grille 12 ans */
.dpp-year-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; margin-bottom: 10px; }
.dpp-year-cell {
  padding: 6px 2px; border: none; border-radius: 7px; background: transparent;
  font-size: 12.5px; font-weight: 500; color: #374151; cursor: pointer;
  font-family: 'Sora', sans-serif; text-align: center; transition: all 0.1s;
}
.dpp-year-cell:hover:not(.active) { background: #f1f5f9; }

/* Grille 12 mois */
.dpp-month-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; margin-bottom: 10px; }
.dpp-month-cell {
  padding: 8px 4px; border: none; border-radius: 8px; background: transparent;
  font-size: 12px; font-weight: 500; color: #374151; cursor: pointer;
  font-family: 'Sora', sans-serif; text-align: center; transition: all 0.1s;
}
.dpp-month-cell:hover:not(.active) { background: #f1f5f9; }

/* Navigation mois dans la vue jours */
.dpp-month-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.dpp-month-btn {
  flex: 1; text-align: center; font-size: 13px; font-weight: 600; color: #1a1d27;
  background: none; border: none; cursor: pointer; display: flex; align-items: center;
  justify-content: center; font-family: 'Sora', sans-serif; padding: 4px 6px;
  border-radius: 7px; transition: background 0.1s; text-transform: capitalize;
}
.dpp-month-btn:hover { background: #f1f5f9; }

/* Jours */
.dpp-weekdays {
  display: grid; grid-template-columns: repeat(7, 1fr);
  margin-bottom: 3px;
}
.dpp-weekdays span { text-align: center; font-size: 9.5px; font-weight: 700; color: #c4cdd6; padding: 2px 0; }
.dpp-days-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; margin-bottom: 10px; }
.dpp-day-cell {
  aspect-ratio: 1; border: none; background: transparent; border-radius: 7px;
  font-size: 12px; font-weight: 500; color: #374151; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.1s; font-family: 'Sora', sans-serif;
}
.dpp-day-cell:hover:not(.dpp-empty):not(.dpp-selected) { background: #f1f5f9; }
.dpp-empty { pointer-events: none; }
.dpp-today { font-weight: 700; }
.dpp-today-btn {
  width: 100%; padding: 8px 0; border: 1px solid #e4e7ef; border-radius: 8px;
  background: transparent; color: #64748b; font-family: 'Sora', sans-serif;
  font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.12s;
}
.dpp-today-btn:hover { background: #f1f5f9; border-color: #3b82f6; color: #3b82f6; }

/* ══ CAL BODY ══ */
.cal-body { flex: 1; display: flex; overflow: hidden; position: relative; }
.cal-loader {
  position: absolute; inset: 0; z-index: 30;
  background: rgba(255,255,255,0.8); backdrop-filter: blur(4px);
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
}
.loader-ring { width: 32px; height: 32px; border-radius: 50%; border: 3px solid #e4e7ef; animation: spin 0.7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.loader-label { font-size: 12.5px; font-weight: 500; color: #94a3b8; }

/* ══ DESKTOP ══ */
.desktop-layout { display: flex; width: 100%; overflow: hidden; }

/* ── Sidebar ── */
.sidebar {
  width: 220px; flex-shrink: 0; background: #fff;
  border-right: 1px solid #e4e7ef; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 2px 0 10px rgba(0,0,0,0.04); z-index: 10;
}
.sidebar-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px 10px; flex-shrink: 0; border-bottom: 1px solid #edf0f4;
}
.sidebar-title { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; color: #9ba3b2; }
.sidebar-count {
  font-size: 11px; font-weight: 700; background: #f4f5f8; color: #64748b;
  padding: 2px 7px; border-radius: 6px; font-family: 'JetBrains Mono', monospace;
}

.tech-cards-list {
  flex: 1; overflow-y: auto; padding: 10px 10px 6px;
  display: flex; flex-direction: column; gap: 6px;
}
.tech-cards-list::-webkit-scrollbar { width: 3px; }
.tech-cards-list::-webkit-scrollbar-thumb { background: #e2e6ed; border-radius: 3px; }

.tech-card {
  position: relative; display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 12px;
  border: 1.5px solid #e4e7ef; background: #fff;
  cursor: pointer; transition: all 0.15s; overflow: hidden;
}
.tech-card:hover { border-color: #c8d0dd; background: #f8f9fb; transform: translateY(-1px); box-shadow: 0 3px 10px rgba(0,0,0,0.06); }
.tech-card:active { transform: scale(0.98); }
.tc-selected { box-shadow: 0 4px 14px rgba(0,0,0,0.08) !important; transform: translateY(-1px) !important; }

/* Avatar */
.tc-avatar {
  width: 36px; height: 36px; border-radius: 10px; color: #fff;
  font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.tc-avatar-all { background: #e4e7ef; color: #64748b; }
.tc-selected .tc-avatar-all { background: #1a1d27; color: #fff; }

/* Meta */
.tc-meta { flex: 1; min-width: 0; }
.tc-name { font-size: 12.5px; font-weight: 700; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tc-sub { font-size: 10.5px; color: #9ba3b2; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Count badge */
.tc-count {
  font-size: 10.5px; font-weight: 700; font-family: 'JetBrains Mono', monospace;
  background: #f4f5f8; color: #94a3b8;
  padding: 3px 7px; border-radius: 6px; flex-shrink: 0;
  transition: all 0.15s;
}

/* Active bar on the left edge */
.tc-active-bar {
  position: absolute; left: 0; top: 6px; bottom: 6px;
  width: 3px; border-radius: 0 3px 3px 0;
}

/* Stat bar at bottom */
.sidebar-stat-bar {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 14px; border-top: 1px solid #edf0f4;
  flex-shrink: 0; background: #fff;
}
.ssb-count { font-size: 14px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.ssb-label { font-size: 11px; color: #9ba3b2; }

/* ── Grid wrapper ── */
.cal-grid-wrap { flex: 1; display: flex; flex-direction: column; overflow: hidden; }

/* ══ DAY VIEW ══ */
.day-view { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.day-tech-header {
  display: flex; background: #fff; border-bottom: 1px solid #e4e7ef; flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04); z-index: 5;
}
.day-time-corner { width: 52px; flex-shrink: 0; border-right: 1px solid #e4e7ef; }
.day-tech-head-cell {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 8px 6px; gap: 4px; border-right: 1px solid #e4e7ef; border-top: 2px solid;
}
.day-tech-head-cell:last-child { border-right: none; }
.dthc-avatar { width: 30px; height: 30px; border-radius: 8px; color: #fff; font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.dthc-name { font-size: 11.5px; font-weight: 700; color: #1a1d27; }
.dthc-count { font-size: 10px; font-family: 'JetBrains Mono', monospace; font-weight: 600; }
.day-body {
  flex: 1; display: flex; overflow-y: auto; background: #f4f5f8;
}
.day-body::-webkit-scrollbar { width: 5px; }
.day-body::-webkit-scrollbar-thumb { background: #d0d5df; border-radius: 5px; }
.day-time-col { width: 52px; flex-shrink: 0; background: #fff; border-right: 1px solid #e4e7ef; }
.day-time-cell { height: 64px; display: flex; align-items: flex-start; justify-content: flex-end; padding: 0 8px; border-bottom: 1px solid #e4e7ef; }
.day-tech-col {
  flex: 1; position: relative; background: #fff; border-right: 1px solid #e4e7ef;
}
.day-tech-col:last-child { border-right: none; }
.day-hour-cell { height: 64px; border-bottom: 1px solid #e4e7ef; }

/* ══ WEEK VIEW ══ */
.week-header {
  display: grid; grid-template-columns: 52px repeat(7, 1fr);
  border-bottom: 1px solid #e4e7ef; background: #fff; flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04); z-index: 5;
}
.wh-corner { border-right: 1px solid #e4e7ef; }
.wh-cell {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 8px 4px; gap: 4px; cursor: pointer; transition: background 0.1s;
  border-right: 1px solid #e4e7ef;
}
.wh-cell:last-child { border-right: none; }
.wh-cell:hover { background: #f7f8fb; }
.wh-dow { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.8px; color: #9ba3b2; }
.wh-num { width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #1a1d27; transition: all 0.12s; }
.wh-today .wh-dow { color: #3b82f6; }
.wh-count { font-size: 9.5px; font-family: 'JetBrains Mono', monospace; font-weight: 600; }

.week-body {
  flex: 1; display: grid; grid-template-columns: 52px repeat(7, 1fr);
  overflow-y: auto; background: #f4f5f8;
}
.week-body::-webkit-scrollbar { width: 5px; }
.week-body::-webkit-scrollbar-thumb { background: #d0d5df; border-radius: 5px; }
.time-col { background: #fff; border-right: 1px solid #e4e7ef; }
.time-cell { height: 64px; display: flex; align-items: flex-start; justify-content: flex-end; padding: 0 8px; border-bottom: 1px solid #e4e7ef; }
.time-label { font-size: 9.5px; font-family: 'JetBrains Mono', monospace; color: #b0b7c3; font-weight: 500; padding-top: 6px; white-space: nowrap; }
.week-day-col { position: relative; border-right: 1px solid #e4e7ef; background: #fff; transition: background 0.1s; }
.week-day-col:last-child { border-right: none; }
.wdc-weekend { background: #fafafa; }
.wdc-today { background: #fafeff; }
.wdc-selected { background: #f0f6ff; }
.week-hour-cell { height: 64px; border-bottom: 1px solid #e4e7ef; pointer-events: none; }

/* ══ NOW LINE ══ */
.now-line { position: absolute; left: 0; right: 0; height: 0; border-top: 2px solid; z-index: 6; pointer-events: none; }
.now-dot { width: 8px; height: 8px; border-radius: 50%; position: absolute; top: -4px; left: -1px; box-shadow: 0 0 0 3px rgba(0,0,0,0.06); }

/* ══ EVENT BLOCKS ══ */
.week-ev {
  position: absolute; left: 2px; right: 2px;
  border: 1px solid; border-radius: 7px;
  display: flex; align-items: stretch; overflow: hidden;
  cursor: pointer; z-index: 2; transition: box-shadow 0.12s, z-index 0s;
}
.week-ev:hover { box-shadow: 0 3px 14px color-mix(in srgb, var(--ec, #3b82f6) 25%, transparent); z-index: 5; }
.wev-accent { width: 4px; flex-shrink: 0; }
.wev-body { flex: 1; padding: 4px 6px; overflow: hidden; display: flex; flex-direction: column; justify-content: flex-start; }
.wev-title { font-size: 11px; font-weight: 700; color: #1a1d27; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wev-time { font-size: 9px; font-family: 'JetBrains Mono', monospace; color: #9ba3b2; margin-top: 1px; white-space: nowrap; }
.wev-client { font-size: 10px; color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.wev-status { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; align-self: flex-start; margin: 5px 5px 0 0; }

/* ══ MONTH VIEW ══ */
.month-header { display: grid; grid-template-columns: repeat(7, 1fr); background: #fff; border-bottom: 1px solid #e4e7ef; flex-shrink: 0; }
.mh-cell { text-align: center; padding: 10px 4px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #9ba3b2; border-right: 1px solid #e4e7ef; }
.mh-cell:last-child { border-right: none; }
.month-grid { flex: 1; display: grid; grid-template-columns: repeat(7, 1fr); overflow-y: auto; background: #f4f5f8; }
.month-grid::-webkit-scrollbar { width: 5px; }
.month-grid::-webkit-scrollbar-thumb { background: #d0d5df; border-radius: 5px; }
.month-cell { border-right: 1px solid #e4e7ef; border-bottom: 1px solid #e4e7ef; min-height: 100px; background: #fff; cursor: pointer; transition: background 0.1s; overflow: hidden; }
.month-cell:nth-child(7n) { border-right: none; }
.mc-other-month { background: #f9fafb; cursor: default; }
.mc-today { background: #fafeff; }
.mc-selected { background: #f0f6ff; }
.mc-weekend { background: #fafafa; }
.mc-inner { padding: 6px 8px; height: 100%; display: flex; flex-direction: column; }
.mc-num-wrap { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.mc-num { width: 26px; height: 26px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #374151; transition: all 0.12s; }
.mc-other-month .mc-num { color: #c8cdd8; }
.mc-ev-count { font-size: 9.5px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.mc-events { display: flex; flex-direction: column; gap: 2px; flex: 1; overflow: hidden; }
.mc-ev-pill { display: flex; align-items: center; gap: 4px; padding: 2px 5px; border-radius: 5px; border: 1px solid; cursor: pointer; transition: opacity 0.1s; overflow: hidden; }
.mc-ev-pill:hover { opacity: 0.8; }
.mc-ev-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
.mc-ev-label { font-size: 10.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }
.mc-ev-more { font-size: 10px; color: #94a3b8; font-weight: 500; cursor: pointer; }

/* ══ STATUS ══ */
.s-planned     { background: #3b82f6; }
.s-in-progress { background: #f59e0b; box-shadow: 0 0 0 2px #fef3c7; }
.s-done        { background: #22c55e; }
.s-cancelled   { background: #ef4444; }

/* ══ MOBILE ══ */
.mobile-layout { flex: 1; display: flex; flex-direction: column; overflow: hidden; }

/* Tech pills */
.mob-tech-strip {
  display: flex; gap: 6px; padding: 8px 10px;
  background: #fff; border-bottom: 1px solid #e4e7ef;
  overflow-x: auto; flex-shrink: 0; scrollbar-width: none;
}
.mob-tech-strip::-webkit-scrollbar { display: none; }
.mts-pill {
  display: flex; align-items: center; gap: 5px; padding: 5px 11px;
  border-radius: 20px; border: 1.5px solid #e4e7ef; background: #f7f8fb;
  font-size: 12px; font-weight: 600; color: #64748b;
  cursor: pointer; flex-shrink: 0; transition: all 0.14s; touch-action: manipulation;
}
.mts-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

/* Week strip */
.mob-week-strip { display: flex; background: #fff; border-bottom: 1px solid #e4e7ef; padding: 6px 8px; gap: 4px; flex-shrink: 0; }
.mws-cell { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 4px 2px; border-radius: 10px; cursor: pointer; touch-action: manipulation; transition: all 0.12s; }
.mws-dow { font-size: 9.5px; font-weight: 600; color: #9ba3b2; text-transform: uppercase; }
.mws-num { width: 30px; height: 30px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #1a1d27; transition: all 0.12s; }
.mws-dot { width: 5px; height: 5px; border-radius: 50%; }

/* Day list */
.mob-day-list { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #f4f5f8; }
.mdl-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px 8px; flex-shrink: 0; }
.mdl-label { font-size: 13.5px; font-weight: 700; color: #1a1d27; text-transform: capitalize; }
.mdl-count { font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 7px; font-family: 'JetBrains Mono', monospace; }
.mdl-scroll { flex: 1; overflow-y: auto; padding: 4px 12px 20px; display: flex; flex-direction: column; gap: 10px; -webkit-overflow-scrolling: touch; }
.mdl-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; color: #c4c9d4; font-size: 13px; padding: 40px 8px; text-align: center; }
.mdl-item { display: flex; align-items: flex-start; gap: 10px; }
.mdl-time-col { display: flex; flex-direction: column; align-items: center; gap: 4px; padding-top: 8px; flex-shrink: 0; width: 36px; }
.mdl-time-start, .mdl-time-end { font-size: 9.5px; font-family: 'JetBrains Mono', monospace; color: #94a3b8; font-weight: 500; white-space: nowrap; }
.mdl-time-bar { width: 2px; flex: 1; min-height: 20px; border-radius: 1px; }
.mdl-card { flex: 1; border-radius: 12px; border: 1px solid #e4e7ef; background: #fff; border-left-width: 4px; padding: 10px 12px; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.04); transition: box-shadow 0.12s; }
.mdl-card:active { transform: scale(0.99); }
.mdl-tech-tag { display: inline-flex; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 5px; margin-bottom: 4px; }
.mdl-card-title { font-size: 14px; font-weight: 700; color: #1a1d27; margin-bottom: 4px; }
.mdl-card-client { font-size: 11.5px; color: #9ba3b2; display: flex; align-items: center; gap: 4px; margin-top: 2px; }
.mdl-card-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.mdl-tag { font-size: 10.5px; padding: 2px 7px; border-radius: 5px; border: 1px solid; font-weight: 600; }

/* Month mobile */
.mob-month-header { display: grid; grid-template-columns: repeat(7, 1fr); background: #fff; border-bottom: 1px solid #e4e7ef; flex-shrink: 0; }
.mmh-cell { text-align: center; padding: 8px 2px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.7px; color: #9ba3b2; }
.mob-month-grid { display: grid; grid-template-columns: repeat(7, 1fr); flex: 1; overflow-y: auto; background: #fff; }
.mob-mc { border-right: 1px solid #e4e7ef; border-bottom: 1px solid #e4e7ef; cursor: pointer; min-height: 52px; }
.mob-mc:nth-child(7n) { border-right: none; }
.mob-mc-other { background: #f9fafb; cursor: default; }
.mob-mc-today { background: #fafeff; }
.mob-mc-selected { background: #f0f6ff; }
.mob-mc-weekend { background: #fafafa; }
.mob-mc-inner { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 6px 2px; }
.mob-mc-num { width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #374151; transition: all 0.12s; }
.mob-mc-other .mob-mc-num { color: #c8cdd8; }
.mob-mc-dots { display: flex; gap: 3px; flex-wrap: wrap; justify-content: center; }
.mob-mc-dot { width: 5px; height: 5px; border-radius: 50%; }

/* Day mini (month) */
.mob-day-mini { border-top: 1px solid #e4e7ef; background: #fff; padding: 10px 12px 20px; flex-shrink: 0; max-height: 200px; overflow-y: auto; }
.mdm-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.mdm-label { font-size: 12px; font-weight: 700; color: #1a1d27; text-transform: capitalize; flex: 1; }
.mdm-count { font-size: 12px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.mdm-empty { font-size: 12px; color: #c4c9d4; text-align: center; padding: 12px 0; }
.mdm-item { display: flex; align-items: stretch; gap: 0; border-radius: 9px; border: 1px solid #e4e7ef; overflow: hidden; cursor: pointer; margin-bottom: 6px; transition: all 0.1s; }
.mdm-item:active { transform: scale(0.98); }
.mdm-stripe { width: 3px; flex-shrink: 0; }
.mdm-body { flex: 1; padding: 8px 10px; }
.mdm-title { font-size: 12.5px; font-weight: 700; color: #1a1d27; }
.mdm-time { font-size: 10.5px; font-family: 'JetBrains Mono', monospace; color: #9ba3b2; margin-top: 2px; }
.mdm-chevron { display: flex; align-items: center; padding: 0 10px; color: #c4cdd6; }

/* ══ SHEET ══ */
.sheet-overlay { position: fixed; inset: 0; z-index: 100; background: rgba(0,0,0,0.45); backdrop-filter: blur(3px); display: flex; align-items: flex-end; }
.sheet-panel { width: 100%; background: #fff; border-radius: 20px 20px 0 0; padding-bottom: env(safe-area-inset-bottom, 16px); max-height: 85dvh; display: flex; flex-direction: column; box-shadow: 0 -8px 32px rgba(0,0,0,0.18); }
.sheet-handle { width: 36px; height: 4px; border-radius: 2px; background: #d0d5df; margin: 10px auto 0; }
.sheet-header { display: flex; align-items: center; gap: 10px; padding: 12px 20px 8px; flex-shrink: 0; border-bottom: 1px solid #edf0f4; }
.sheet-title { font-size: 16px; font-weight: 700; color: #1a1d27; flex: 1; }
.sheet-close { width: 30px; height: 30px; border-radius: 8px; border: 1px solid #e4e7ef; background: #f7f8fb; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.sheet-dp-body { padding: 14px 20px 20px; overflow-y: auto; }

/* ══ TRANSITIONS ══ */
.dp-pop-enter-active { transition: opacity 0.15s, transform 0.15s; }
.dp-pop-leave-active { transition: opacity 0.1s, transform 0.1s; }
.dp-pop-enter-from, .dp-pop-leave-to { opacity: 0; }
.dp-pop-enter-from .dp-popover, .dp-pop-leave-to .dp-popover { transform: translateY(-6px) scale(0.97); }
.sheet-enter-active { transition: opacity 0.22s ease; }
.sheet-leave-active { transition: opacity 0.18s ease; }
.sheet-enter-from, .sheet-leave-to { opacity: 0; }
.sheet-enter-active .sheet-panel { transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1); }
.sheet-enter-from .sheet-panel { transform: translateY(100%); }
</style>